/**
 * useApiMutation — POST/PATCH/PUT/DELETE 요청 공통 훅 (React Query useMutation 래퍼)
 *
 * 주요 기능:
 * - 동일 URL+Method 중복 요청 자동 차단 (inFlight)
 * - 요청 시작/종료 시 onPendingChange 콜백 (전역 로딩 스피너 연동)
 * - 성공 후 refreshKeys 자동 invalidate
 * - 에러 발생 시 onError 콜백 (configure에서 설정한 핸들러 호출)
 *
 * 호출 방식 A — 훅에 url/method 고정:
 * @example
 * const mutation = useApiMutation({ url: "/api/members", method: "post" });
 * mutation.mutate({ name: "홍길동", email: "hong@link-up.kr" });
 *
 * 호출 방식 B — mutate 시점에 url/method 결정 (url/method 생략):
 * @example
 * const mutation = useApiMutation({});
 * mutation.mutate({ url: "/api/members/1", method: "patch", data: { name: "홍길동" } });
 *
 * refreshKeys 사용:
 * @example
 * const mutation = useApiMutation({
 *   url: "/api/members",
 *   method: "post",
 *   refreshKeys: ["members", "members-stats"],
 * });
 */
import {
    useMutation,
    useQueryClient,
    type UseMutationOptions,
    type UseMutationResult,
} from "@tanstack/react-query";

import { getConfig } from "./configure";

export type HttpMethod = "post" | "patch" | "put" | "delete";

/**
 * mutate() 에 url/method/data 를 직접 넘기는 동적 변수 형태.
 * 훅에 url/method 를 고정하지 않을 때 사용한다.
 */
export interface DynamicMutationVariables<TData = unknown> {
    url: string;
    method: HttpMethod;
    data?: TData;
    /** 이 mutate 호출에만 적용할 캐시 무효화 키 (훅의 refreshKeys 보다 우선) */
    refreshKeys?: string[];
}

export interface UseApiMutationProps<TVariables = unknown, TResult = unknown> {
    /** 요청 URL. 훅 레벨에서 고정할 때 사용. */
    url?: string;
    /** HTTP 메서드. 훅 레벨에서 고정할 때 사용. */
    method?: HttpMethod;
    /**
     * 성공 후 무효화할 React Query 키 목록 (문자열 배열).
     * @example refreshKeys={["members", "members-stats"]}
     */
    refreshKeys?: string[];
    /** 성공 콜백 (onSuccess) */
    onSuccess?: (data: TResult, variables: TVariables) => void;
    /** 에러 콜백 — configure 의 onError 에 추가로 호출된다 */
    onError?: (error: Error, variables: TVariables) => void;
    /** useMutation 나머지 옵션 (mutationFn / onSuccess / onError 제외) */
    options?: Omit<
        UseMutationOptions<TResult, Error, TVariables>,
        "mutationFn" | "onSuccess" | "onError"
    >;
}

/** 동적 변수 형태인지 판별 */
function isDynamic(v: unknown): v is DynamicMutationVariables {
    return (
        typeof v === "object" &&
        v !== null &&
        "url" in v &&
        "method" in v
    );
}

/** 중복 요청 추적 */
const inFlight = new Set<string>();
const makeKey = (method: string, url: string) =>
    `${method.toUpperCase()}:${url}`;

export function useApiMutation<TVariables = unknown, TResult = unknown>({
    url: hookUrl,
    method: hookMethod,
    refreshKeys: hookRefreshKeys,
    onSuccess: externalOnSuccess,
    onError: externalOnError,
    options,
}: UseApiMutationProps<TVariables, TResult>): UseMutationResult<
    TResult,
    Error,
    TVariables
> {
    const queryClient = useQueryClient();
    const { axios, onError, onPendingChange } = getConfig();

    return useMutation<TResult, Error, TVariables>({
        ...options,
        mutationFn: async (variables) => {
            // 동적 변수(url/method 포함) vs 단순 payload 판별
            const dynamic = isDynamic(variables);
            const targetUrl = dynamic ? variables.url : hookUrl;
            const targetMethod = dynamic ? variables.method : hookMethod;
            const payload = dynamic ? variables.data : variables;

            if (!targetUrl || !targetMethod) {
                throw new Error(
                    "[api-hooks] url 과 method 가 필요합니다. " +
                    "훅 인자 또는 mutate() 인자로 전달하세요."
                );
            }

            const key = makeKey(targetMethod, targetUrl);

            // 동일 요청 중복 차단
            if (inFlight.has(key)) {
                throw new Error("처리 중인 요청이 있습니다. 잠시 후 다시 시도하세요.");
            }

            // 요청 시작 등록 (pending 증가는 try 진입 전)
            inFlight.add(key);
            onPendingChange?.(1);

            try {
                const res =
                    targetMethod === "delete"
                        ? await axios.delete(targetUrl, { data: payload })
                        : await axios[targetMethod](targetUrl, payload);

                return res.data as TResult;
            } finally {
                inFlight.delete(key);
                onPendingChange?.(-1);
            }
        },

        onSuccess: (data, variables, mutation) => {
            // 캐시 무효화: mutate 시점 변수의 refreshKeys > 훅의 refreshKeys
            const dynamic = isDynamic(variables);
            const keys =
                (dynamic ? variables.refreshKeys : undefined) ?? hookRefreshKeys;

            if (keys?.length) {
                keys.forEach((key) =>
                    queryClient.invalidateQueries({ queryKey: [key], exact: false })
                );
            }

            externalOnSuccess?.(data, variables);
        },

        onError: (error, variables) => {
            if (process.env.NODE_ENV === "development") {
                console.error("[api-hooks] mutation error:", error);
            }
            onError?.(error);
            externalOnError?.(error as Error, variables);
        },
    });
}
